import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CornerDownRight, MessageSquare } from 'lucide-react';
import { commentSchema, formatDateTime } from '@blimo/shared';
import { Badge } from './badge.jsx';
import { Button } from './button.jsx';
import { applyProblemToForm, Form, FormRootError, TextareaField } from './form.jsx';
import { cn } from '../lib/utils.js';

/**
 * Comment form (RHF + Zod for UX; the API validates). `onSubmit` returns a promise; the field is cleared on success.
 * @param {{ onSubmit: (body: string) => Promise<unknown>, label?: string, submitLabel?: string, placeholder?: string, onCancel?: () => void, className?: string }} props
 */
export function CommentForm({ onSubmit, label = 'Kommentar', submitLabel = 'Kommentieren', placeholder = 'Ihr Kommentar …', onCancel, className }) {
  const form = useForm({ resolver: zodResolver(commentSchema), defaultValues: { body: '' } });
  const submit = form.handleSubmit(async ({ body }) => {
    try {
      await onSubmit(body);
      form.reset({ body: '' });
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['body'] });
    }
  });
  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className={cn('flex flex-col gap-2', className)}>
        <FormRootError />
        <TextareaField name="body" label={label} rows={3} placeholder={placeholder} />
        <div className="flex gap-2">
          <Button type="submit" size="sm" loading={form.formState.isSubmitting}>
            {submitLabel}
          </Button>
          {onCancel && (
            <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
              Abbrechen
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}

/**
 * Comment threads (CommentResource with `replies`). Hidden comments (`is_active = false`) are marked.
 * @param {{
 *   comments?: any[],
 *   onReply?: (parent: any, body: string) => Promise<unknown>,
 *   actions?: (comment: any) => import('react').ReactNode,
 *   meta?: (comment: any) => import('react').ReactNode,
 *   emptyText?: string,
 *   label?: string,
 * }} props
 */
export function CommentThreads({ comments, onReply, actions, meta, emptyText = 'Noch keine Kommentare.', label = 'Kommentare' }) {
  const [replyTo, setReplyTo] = useState(/** @type {number|null} */ (null));
  if (!comments?.length) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <MessageSquare className="size-4" aria-hidden="true" /> {emptyText}
      </p>
    );
  }
  return (
    <ul aria-label={label} className="flex flex-col gap-3">
      {comments.map((comment) => (
        <li key={comment.id} className="flex flex-col gap-2">
          <CommentItem comment={comment} actions={actions} meta={meta} />
          {comment.replies?.length > 0 && (
            <ul aria-label={`Antworten auf Kommentar von ${comment.author?.name ?? 'Unbekannt'}`} className="ml-5 flex flex-col gap-2 border-l pl-3">
              {comment.replies.map((reply) => (
                <li key={reply.id}>
                  <CommentItem comment={reply} actions={actions} reply />
                </li>
              ))}
            </ul>
          )}
          {onReply &&
            (replyTo === comment.id ? (
              <CommentForm
                className="ml-5"
                label="Antwort"
                submitLabel="Antworten"
                placeholder="Ihre Antwort …"
                onSubmit={async (body) => {
                  await onReply(comment, body);
                  setReplyTo(null);
                }}
                onCancel={() => setReplyTo(null)}
              />
            ) : (
              <Button type="button" variant="link" size="sm" className="ml-5 w-fit" onClick={() => setReplyTo(comment.id)}>
                <CornerDownRight aria-hidden="true" /> Antworten
              </Button>
            ))}
        </li>
      ))}
    </ul>
  );
}

function CommentItem({ comment, actions, meta, reply = false }) {
  const hidden = comment.is_active === false;
  return (
    <article
      aria-label={`${reply ? 'Antwort' : 'Kommentar'} von ${comment.author?.name ?? 'Unbekannt'}`}
      data-slot="comment"
      className={cn('flex flex-col gap-1 rounded-md border bg-card px-3 py-2 text-sm', hidden && 'border-dashed opacity-70')}
    >
      <header className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{comment.author?.name ?? 'Unbekannt'}</span>
        <span className="numeric">{formatDateTime(comment.created_at)}</span>
        {comment.is_mine && <Badge variant="secondary">Eigener Kommentar</Badge>}
        {hidden && <Badge variant="neutral">Ausgeblendet</Badge>}
        {meta?.(comment)}
        <span className="ml-auto flex gap-1">{actions?.(comment)}</span>
      </header>
      <p className="whitespace-pre-wrap break-words">{comment.body}</p>
    </article>
  );
}
